import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'

import { organizationsOrganizationsRetrieveOptions } from 'src/api/main'
import { OrgChartTree } from 'src/features/workspaces/pages/WorkspaceMembersHome/OrgChartTree'
import type { OrgChartNode } from 'src/features/workspaces/pages/WorkspaceMembersHome/types'
import { composeDisplayName } from 'src/utils/string'

import type { OrgUnit } from '../types'
import { apiTypeToKind } from '../utils'

type Props = {
  workspaceId: number
  units: OrgUnit[]
  onSelect: (unit: OrgUnit) => void
}

const collectDefaultCollapsed = (
  node: OrgChartNode,
  depth: number,
  maxVisible: number,
  result: Set<string>
) => {
  if (depth >= maxVisible && node.children.length > 0) {
    result.add(node.id)
  }
  for (const child of node.children) {
    collectDefaultCollapsed(child, depth + 1, maxVisible, result)
  }
}

const buildTree = (orgName: string, units: OrgUnit[]): OrgChartNode => {
  const nodeMap = new Map<number, OrgChartNode>()

  for (const u of units) {
    nodeMap.set(u.id, {
      id: String(u.id),
      name: u.name,
      type: apiTypeToKind(u.type),
      owner: u.head_user_detail
        ? {
            name: composeDisplayName(
              u.head_user_detail.first_name,
              u.head_user_detail.last_name,
              u.head_user_detail.email
            ),
            title: u.head_user_detail.email
          }
        : undefined,
      memberCount: u.users_count,
      childCount: 0,
      children: []
    })
  }

  const rootChildren: OrgChartNode[] = []
  for (const u of units) {
    const node = nodeMap.get(u.id)
    if (!node) continue
    if (u.parent != null) {
      const parentNode = nodeMap.get(u.parent)
      if (parentNode) parentNode.children.push(node)
      else rootChildren.push(node)
    } else {
      rootChildren.push(node)
    }
  }

  for (const n of nodeMap.values()) n.childCount = n.children.length
  rootChildren.sort((a, b) => a.name.localeCompare(b.name))

  const totalMembers = rootChildren.reduce((s, c) => s + c.memberCount, 0)

  return {
    id: 'company-root',
    name: orgName,
    type: 'company',
    memberCount: totalMembers,
    childCount: rootChildren.length,
    children: rootChildren
  }
}

export const OrgChartViewCard = ({ workspaceId, units, onSelect }: Props) => {
  const { data: organization } = useQuery({
    ...organizationsOrganizationsRetrieveOptions({
      path: { id: workspaceId }
    }),
    enabled: Number.isFinite(workspaceId)
  })

  const tree = useMemo(
    () => buildTree(organization?.name ?? 'Organization', units),
    [organization, units]
  )

  const unitById = useMemo(() => {
    const m = new Map<string, OrgUnit>()
    for (const u of units) m.set(String(u.id), u)
    return m
  }, [units])

  const [userCollapsedIds, setUserCollapsedIds] = useState<Set<string> | null>(
    null
  )

  const defaultCollapsedIds = useMemo(() => {
    const defaults = new Set<string>()
    collectDefaultCollapsed(tree, 0, 1, defaults)
    return defaults
  }, [tree])

  const collapsedIds = userCollapsedIds ?? defaultCollapsedIds

  const handleToggle = useCallback(
    (id: string) => {
      setUserCollapsedIds((prev) => {
        const base = prev ?? defaultCollapsedIds
        const next = new Set(base)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
    },
    [defaultCollapsedIds]
  )

  const handleSelect = (id: string) => {
    const unit = unitById.get(id)
    if (unit) onSelect(unit)
  }

  return (
    <OrgChartTree
      tree={tree}
      selectedNodeId={null}
      collapsedIds={collapsedIds}
      onSelectNode={handleSelect}
      onToggleCollapse={handleToggle}
      bordered={false}
    />
  )
}
